import { format } from "date-fns";
import { ArrowLeft, Check, ChevronDown, ChevronRight, X } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import EmptyState from "../../../shared/components/EmptyState";
import LoadingOverlay from "../../../shared/components/LoadingOverlay";
import PageHeader from "../../../shared/components/PageHeader";
import { useModeratePanel, usePanelDetail } from "../hooks/usePanels";
import PanelStatusBadge from "./PanelStatusBadge";

const formatPrice = (priceArs?: number | null) => {
  if (priceArs == null || priceArs === 0) return "—";
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    maximumFractionDigits: 0,
  }).format(priceArs);
};

const PanelDetail = () => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const { slug } = useParams<{ slug: string }>();

  const { data: panel, isLoading, isError, error } = usePanelDetail(slug);
  const moderateMutation = useModeratePanel();

  const [showTemplate, setShowTemplate] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [rejectNote, setRejectNote] = useState("");
  const [feedback, setFeedback] = useState<{
    kind: "success" | "error";
    message: string;
  } | null>(null);

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title={t("PANELS.detailTitle", "Panel")} />
        <div className="py-12">
          <LoadingOverlay />
        </div>
      </div>
    );
  }

  if (isError || !panel) {
    const isNotFound =
      error && typeof error === "object" && "response" in error
        ? (error as { response?: { status?: number } }).response?.status === 404
        : false;
    return (
      <div className="flex flex-col gap-6">
        <PageHeader title={t("PANELS.detailTitle", "Panel")} />
        <EmptyState
          title={
            isNotFound
              ? t("PANELS.notFoundTitle", "Panel not found")
              : t("PANELS.errorTitle", "Could not load panel")
          }
          description={
            isNotFound
              ? t(
                  "PANELS.notFoundDescription",
                  "The panel you are looking for does not exist.",
                )
              : error instanceof Error
                ? error.message
                : t("PANELS.errorDescription", "Try again in a moment.")
          }
          actions={
            <Button
              asChild
              variant="outline"
              className="border-neutral-700 text-neutral-300"
            >
              <Link to="/panels">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t("PANELS.backToList", "Back to list")}
              </Link>
            </Button>
          }
        />
      </div>
    );
  }

  const handleApprove = () => {
    moderateMutation.mutate(
      { id: panel.id, body: { decision: "approved" } },
      {
        onSuccess: () => {
          setFeedback({
            kind: "success",
            message: t("PANELS.approvedToast", "Panel approved."),
          });
          setTimeout(() => navigate("/panels"), 600);
        },
        onError: (err) => {
          setFeedback({
            kind: "error",
            message:
              err instanceof Error
                ? err.message
                : t(
                    "PANELS.moderationError",
                    "Could not save moderation decision.",
                  ),
          });
        },
      },
    );
  };

  const handleReject = () => {
    moderateMutation.mutate(
      {
        id: panel.id,
        body: { decision: "rejected", note: rejectNote.trim() || undefined },
      },
      {
        onSuccess: () => {
          setRejectModalOpen(false);
          setFeedback({
            kind: "success",
            message: t("PANELS.rejectedToast", "Panel rejected."),
          });
          setTimeout(() => navigate("/panels"), 600);
        },
        onError: (err) => {
          setFeedback({
            kind: "error",
            message:
              err instanceof Error
                ? err.message
                : t(
                    "PANELS.moderationError",
                    "Could not save moderation decision.",
                  ),
          });
        },
      },
    );
  };

  const isPending = panel.status === "pending";

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={panel.name}
        subtitle={`v${panel.version} • ${panel.authorName}`}
        actions={
          <div className="flex items-center gap-2">
            <Button
              asChild
              variant="outline"
              className="border-neutral-700 text-neutral-300"
            >
              <Link to="/panels">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t("PANELS.backToList", "Back to list")}
              </Link>
            </Button>
            {isPending && (
              <>
                <Button
                  onClick={() => setRejectModalOpen(true)}
                  variant="outline"
                  className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                  disabled={moderateMutation.isPending}
                >
                  <X className="mr-2 h-4 w-4" />
                  {t("PANELS.reject", "Reject")}
                </Button>
                <Button
                  onClick={handleApprove}
                  className="bg-green-600 text-white hover:bg-green-700"
                  disabled={moderateMutation.isPending}
                >
                  <Check className="mr-2 h-4 w-4" />
                  {t("PANELS.approve", "Approve")}
                </Button>
              </>
            )}
          </div>
        }
      />

      {feedback && (
        <div
          className={
            feedback.kind === "success"
              ? "rounded-md border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm text-green-300"
              : "rounded-md border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300"
          }
        >
          {feedback.message}
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <Card className="border-neutral-800 bg-neutral-900/50 lg:col-span-2">
          <CardHeader>
            <CardTitle className="text-neutral-100">
              {t("PANELS.previewSection", "Preview")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <p className="mb-2 text-xs uppercase tracking-wide text-neutral-500">
                  {t("PANELS.thumbnail", "Thumbnail")}
                </p>
                {panel.previewThumbnailUrl ? (
                  <img
                    src={panel.previewThumbnailUrl}
                    alt={panel.name}
                    className="aspect-video w-full rounded-md border border-neutral-800 bg-neutral-950 object-contain"
                  />
                ) : (
                  <div className="flex aspect-video w-full items-center justify-center rounded-md border border-dashed border-neutral-800 bg-neutral-950 text-sm text-neutral-500">
                    {t("PANELS.noPreview", "No preview")}
                  </div>
                )}
              </div>
              <div>
                <p className="mb-2 text-xs uppercase tracking-wide text-neutral-500">
                  {t("PANELS.animatedPreview", "Animated preview")}
                </p>
                {panel.previewAnimatedUrl ? (
                  <video
                    src={panel.previewAnimatedUrl}
                    controls
                    loop
                    autoPlay
                    muted
                    className="aspect-video w-full rounded-md border border-neutral-800 bg-neutral-950 object-contain"
                  />
                ) : (
                  <div className="flex aspect-video w-full items-center justify-center rounded-md border border-dashed border-neutral-800 bg-neutral-950 text-sm text-neutral-500">
                    {t("PANELS.noAnimated", "No animated preview")}
                  </div>
                )}
              </div>
            </div>

            {panel.description && (
              <div>
                <p className="mb-2 text-xs uppercase tracking-wide text-neutral-500">
                  {t("PANELS.description", "Description")}
                </p>
                <p className="whitespace-pre-wrap text-sm text-neutral-200">
                  {panel.description}
                </p>
              </div>
            )}

            <div>
              <button
                type="button"
                onClick={() => setShowTemplate((s) => !s)}
                className="flex items-center gap-2 text-sm text-neutral-300 hover:text-neutral-100"
              >
                {showTemplate ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
                {t("PANELS.rawTemplate", "Raw template JSON")}
              </button>
              {showTemplate && (
                <pre className="mt-2 max-h-[400px] overflow-auto rounded-md border border-neutral-800 bg-neutral-950 p-3 text-xs text-neutral-300">
                  {JSON.stringify(panel.template, null, 2)}
                </pre>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="h-fit border-neutral-800 bg-neutral-900/50">
          <CardHeader>
            <CardTitle className="text-neutral-100">
              {t("PANELS.metadata", "Metadata")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3 text-sm">
            <Field
              label={t("PANELS.colStatus", "Status")}
              value={<PanelStatusBadge status={panel.status} />}
            />
            <Field label={t("PANELS.slug", "Slug")} value={panel.slug} />
            <Field
              label={t("PANELS.colCategory", "Category")}
              value={
                <Badge
                  variant="outline"
                  className="border-neutral-600 text-neutral-300"
                >
                  {panel.category}
                </Badge>
              }
            />
            <Field
              label={t("PANELS.colLicense", "License")}
              value={panel.license}
            />
            <Field
              label={t("PANELS.colPrice", "Price")}
              value={formatPrice(panel.priceArs)}
            />
            <Field
              label={t("PANELS.colAuthor", "Author")}
              value={panel.authorName}
            />
            <Field
              label={t("PANELS.installCount", "Install count")}
              value={panel.installCount.toLocaleString("es-AR")}
            />
            <Field
              label={t("PANELS.colSubmitted", "Submitted")}
              value={format(new Date(panel.createdAt), "dd MMM yyyy HH:mm")}
            />
            <Field
              label={t("PANELS.updatedAt", "Updated")}
              value={format(new Date(panel.updatedAt), "dd MMM yyyy HH:mm")}
            />
            {panel.tags.length > 0 && (
              <Field
                label={t("PANELS.tags", "Tags")}
                value={
                  <div className="flex flex-wrap gap-1">
                    {panel.tags.map((tag) => (
                      <Badge
                        key={tag}
                        variant="outline"
                        className="border-neutral-700 text-neutral-300"
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                }
              />
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog
        open={rejectModalOpen}
        onOpenChange={(open) => {
          if (!open) {
            setRejectModalOpen(false);
            setRejectNote("");
          }
        }}
      >
        <DialogContent className="border-neutral-800 bg-neutral-900">
          <DialogHeader>
            <DialogTitle className="text-neutral-100">
              {t("PANELS.rejectModalTitle", "Reject panel")}
            </DialogTitle>
            <DialogDescription className="text-neutral-400">
              {t(
                "PANELS.rejectModalDescription",
                "Optionally include a note to share with the author.",
              )}
            </DialogDescription>
          </DialogHeader>
          <textarea
            value={rejectNote}
            onChange={(e) => setRejectNote(e.target.value)}
            placeholder={t(
              "PANELS.rejectNotePlaceholder",
              "Reason for rejection...",
            )}
            rows={4}
            className="w-full rounded-md border border-neutral-700 bg-neutral-800/50 p-3 text-sm text-neutral-200 placeholder:text-neutral-500"
          />
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRejectModalOpen(false);
                setRejectNote("");
              }}
              className="border-neutral-700 text-neutral-300 hover:bg-neutral-800"
            >
              {t("PANELS.cancel", "Cancel")}
            </Button>
            <Button
              onClick={handleReject}
              disabled={moderateMutation.isPending}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              {t("PANELS.confirmReject", "Reject")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

interface FieldProps {
  label: string;
  value: React.ReactNode;
}

const Field = ({ label, value }: FieldProps) => (
  <div className="flex flex-col gap-1">
    <span className="text-xs uppercase tracking-wide text-neutral-500">
      {label}
    </span>
    <div className="text-neutral-200">{value}</div>
  </div>
);

export default PanelDetail;
